import cx from 'classnames'
import { isNil } from 'lodash'
import { useMemo, useState } from 'react'

import { useNavigate } from 'features/i18n/router'
import { Post } from 'lib/_types'
import { breakHighlightText } from 'lib/helpers'
import { extractPost } from './helpers'

import { useLangDetect } from 'hooks/useLangDetect'
import Ellipsis from 'components/Ellipsis'
import { Text } from './Text'

import './style.scss'

type Props = {
  post: Post
  isShared?: boolean
  postLink?: string
  highlight?: string
  isFromTimeline?: boolean
  onSavePreloadedPosts?: Function
}

const PostText = ({
  post,
  isShared,
  postLink,
  highlight,
  isFromTimeline,
  onSavePreloadedPosts,
}: Props) => {
  const { detectLang } = useLangDetect()
  if (isNil(post?.text) || post?.text?.length === 0) {
    return null
  }
  const navigate = useNavigate()

  const texts = useMemo(() => extractPost(post), [post])
  const highlightText = useMemo(
    () => breakHighlightText(highlight),
    [highlight]
  )

  const [__isSelecting, __isSelectingSet] = useState(false)

  const content = (
    <div
      className={cx('PostText', {
        'PostText--selecting': __isSelecting,
        [`PostText--color-${post.color}`]: post.color,
      })}
      onMouseDown={() => {
        __isSelectingSet(true)
      }}
      onMouseUp={() => {
        if (window?.getSelection?.()?.toString?.()?.length > 0) {
          __isSelectingSet(false)
        } else {
          if (postLink) {
            // the current location is the main timeline / or a circle timeline /group/GROUP_ID
            if (isFromTimeline && onSavePreloadedPosts) {
              // console.log('PostText: requesting save preloaded posts')
              onSavePreloadedPosts()
            }
            navigate(postLink)
          }
        }
      }}
      onMouseLeave={() => {
        __isSelectingSet(false)
      }}
      title={
        window.gon.ENV === 'development' || window.gon.ENV === 'staging'
          ? `[${post.language ?? detectLang(post?.text)}]`
          : undefined
      }
    >
      <p
        className={cx(
          'PostText__p',
          post.font_size && `PostText__p--fz-${post.font_size}`
        )}
      >
        {texts.map((text, i) => (
          <Text key={i} text={text} highlightText={highlightText} />
        ))}
      </p>
    </div>
  )

  if (isShared) return content

  return <Ellipsis>{content}</Ellipsis>
}

export default PostText
